import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Mail, 
  MessageSquare, 
  Archive, 
  Eye,
  Edit,
  Trash2,
  ArchiveRestore,
  Search,
  Filter,
  LogOut,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  Image,
  Plus,
  Save,
  Tag,
  Send,
  FileText,
  History,
  Calendar,
  Pencil,
  Paperclip
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import AdminEventsTab from '../components/admin/AdminEventsTab';
import EditContactForm from '../components/admin/EditContactForm';
import GalleryItemForm from '../components/admin/GalleryItemForm';
import NewsletterComposer from '../components/admin/NewsletterComposer';
import EditAudienceForm from '../components/admin/EditAudienceForm';
import ContactsTab from '../components/admin/ContactsTab';
import GalleryTab from '../components/admin/GalleryTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subjectLabels = {
  general: 'General Inquiry',
  events: 'AMS Events',
  university: 'AMS University',
  compass: 'AMS Compass',
  vendor: 'Become a Vendor',
  partnership: 'Partnership',
  import: 'Imported Contact',
};

const MAX_GALLERY_ITEMS = 40;

export default function AdminDashboardPage() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Gallery state
  const [activeTab, setActiveTab] = useState('contacts');
  const [contactView, setContactView] = useState('list'); // 'list' | 'board'
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryStats, setGalleryStats] = useState({ total: 0, max_items: MAX_GALLERY_ITEMS, can_add: true });
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [deleteGalleryConfirm, setDeleteGalleryConfirm] = useState(null);
  const [showAddGalleryDialog, setShowAddGalleryDialog] = useState(false);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  
  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [isLoadingNewsletters, setIsLoadingNewsletters] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [showNewsletterComposer, setShowNewsletterComposer] = useState(false);
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);

  // Audience state
  const [audiences, setAudiences] = useState([]);
  const [showAudienceManager, setShowAudienceManager] = useState(false);
  const [editingAudience, setEditingAudience] = useState(null);
  const [deleteAudienceConfirm, setDeleteAudienceConfirm] = useState(null);

  // Email template editor state (works for any template key: welcome, event_signup_juried, event_signup_open, …)
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [welcomeTemplate, setWelcomeTemplate] = useState(null);
  const [currentTemplateKey, setCurrentTemplateKey] = useState('welcome');
  const [templateForm, setTemplateForm] = useState({ subject: '', body_html: '', body_text: '' });
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSendingTemplateTest, setIsSendingTemplateTest] = useState(false);
  const [templateTestEmail, setTemplateTestEmail] = useState('');
  
  // Bulk tag management state
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
  const [bulkTagOperation, setBulkTagOperation] = useState('add'); // 'add', 'replace', 'remove'
  const [bulkTags, setBulkTags] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showTagManagement, setShowTagManagement] = useState(false);
  const [tagToRename, setTagToRename] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  
  const navigate = useNavigate();

  const token = localStorage.getItem('ams_admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, showArchived]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts?include_archived=${showArchived}`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders())
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ams_admin_token');
        localStorage.removeItem('ams_admin_user');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ams_admin_token');
    localStorage.removeItem('ams_admin_user');
    navigate('/admin');
  };

  const handleMarkRead = async (contactId) => {
    try {
      await axios.post(`${API}/admin/contacts/${contactId}/read`, {}, getAuthHeaders());
      toast.success('Marked as read');
      fetchData();
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const handleArchive = async (contactId) => {
    try {
      await axios.post(`${API}/admin/contacts/${contactId}/archive`, {}, getAuthHeaders());
      toast.success('Contact archived');
      setSelectedContact(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to archive contact');
    }
  };

  const handleUnarchive = async (contactId) => {
    try {
      await axios.post(`${API}/admin/contacts/${contactId}/unarchive`, {}, getAuthHeaders());
      toast.success('Contact restored');
      fetchData();
    } catch (err) {
      toast.error('Failed to restore contact');
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, getAuthHeaders());
      toast.success('Contact deleted permanently');
      setDeleteConfirm(null);
      setSelectedContact(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  const handleUpdate = async (contactId, data) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}`, data, getAuthHeaders());
      toast.success('Contact updated');
      setEditingContact(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update contact');
    }
  };

  // Import functions
  const handleDownloadSample = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts/export/sample-csv`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ams_contacts_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Sample CSV downloaded');
    } catch (err) {
      toast.error('Failed to download sample CSV');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/admin/contacts/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} contacts`);
        fetchData(); // Refresh the contacts list
      } else {
        toast.warning('No contacts were imported');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Import failed';
      toast.error(errorMsg);
      setImportResult({
        success: false,
        total_rows: 0,
        imported: 0,
        skipped: 0,
        errors: [errorMsg]
      });
    } finally {
      setIsImporting(false);
    }
  };

  const resetImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Gallery functions
  const fetchGallery = async () => {
    setIsLoadingGallery(true);
    try {
      const response = await axios.get(`${API}/admin/gallery`, getAuthHeaders());
      setGalleryItems(response.data.items);
      setGalleryStats({
        total: response.data.total,
        max_items: response.data.max_items,
        can_add: response.data.can_add
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ams_admin_token');
        localStorage.removeItem('ams_admin_user');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch gallery');
      }
    } finally {
      setIsLoadingGallery(false);
    }
  };

  const handleUpdateGalleryItem = async (itemId, data) => {
    try {
      await axios.put(`${API}/admin/gallery/${itemId}`, data, getAuthHeaders());
      toast.success('Gallery item updated');
      setEditingGalleryItem(null);
      fetchGallery();
    } catch (err) {
      toast.error('Failed to update gallery item');
    }
  };

  const handleDeleteGalleryItem = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, getAuthHeaders());
      toast.success('Gallery item deleted');
      setDeleteGalleryConfirm(null);
      fetchGallery();
    } catch (err) {
      toast.error('Failed to delete gallery item');
    }
  };

  const handleAddGalleryItem = async (data) => {
    try {
      await axios.post(`${API}/admin/gallery`, data, getAuthHeaders());
      toast.success('Gallery item added');
      setShowAddGalleryDialog(false);
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add gallery item');
    }
  };

  // Fetch gallery when tab changes
  useEffect(() => {
    if (activeTab === 'gallery' && token) {
      fetchGallery();
    }
  }, [activeTab, token]);

  // Newsletter functions
  const fetchNewsletters = async () => {
    setIsLoadingNewsletters(true);
    try {
      const [newslettersRes, tagsRes] = await Promise.all([
        axios.get(`${API}/admin/newsletters`, getAuthHeaders()),
        axios.get(`${API}/admin/tags`, getAuthHeaders())
      ]);
      setNewsletters(newslettersRes.data);
      setAvailableTags(tagsRes.data.tags || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ams_admin_token');
        localStorage.removeItem('ams_admin_user');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch newsletters');
      }
    } finally {
      setIsLoadingNewsletters(false);
    }
  };

  const handleSaveNewsletter = async (data, isNew = false) => {
    try {
      if (isNew) {
        const response = await axios.post(`${API}/admin/newsletters`, data, getAuthHeaders());
        toast.success('Newsletter draft saved');
        setShowNewsletterComposer(false);
        fetchNewsletters();
        return response.data.newsletter;
      } else {
        await axios.put(`${API}/admin/newsletters/${data.id}`, data, getAuthHeaders());
        toast.success('Newsletter updated');
        setEditingNewsletter(null);
        fetchNewsletters();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save newsletter');
    }
  };

  const handleDeleteNewsletter = async (id) => {
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, getAuthHeaders());
      toast.success('Newsletter deleted');
      fetchNewsletters();
    } catch (err) {
      toast.error('Failed to delete newsletter');
    }
  };

  const handlePreviewRecipients = async (id) => {
    try {
      const response = await axios.get(`${API}/admin/newsletters/${id}/preview`, getAuthHeaders());
      setNewsletterPreview(response.data);
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  const handleSendNewsletter = async (id) => {
    try {
      const response = await axios.post(`${API}/admin/newsletters/${id}/send`, {}, getAuthHeaders());
      toast.success(response.data.message);
      setSendConfirm(null);
      fetchNewsletters();
      // Re-fetch a few times while the background task delivers emails so sent/failed counts populate
      setTimeout(() => fetchNewsletters(), 30 * 1000);
      setTimeout(() => fetchNewsletters(), 90 * 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send newsletter');
    }
  };

  // Audience handlers
  const fetchAudiences = async () => {
    try {
      const res = await axios.get(`${API}/admin/audiences`, getAuthHeaders());
      setAudiences(res.data || []);
    } catch (err) {
      // silent: audiences are optional
    }
  };

  const handleSaveAudience = async ({ name, description, filter }) => {
    try {
      const res = await axios.post(`${API}/admin/audiences`, { name, description, filter }, getAuthHeaders());
      toast.success(res.data.message || 'Audience saved');
      fetchAudiences();
      return res.data.audience;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save audience');
      return null;
    }
  };

  const handleUpdateAudience = async (id, data) => {
    try {
      const res = await axios.put(`${API}/admin/audiences/${id}`, data, getAuthHeaders());
      toast.success(res.data.message || 'Audience updated');
      fetchAudiences();
      return res.data.audience;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update audience');
      return null;
    }
  };

  const handleDeleteAudience = async (id) => {
    try {
      const res = await axios.delete(`${API}/admin/audiences/${id}`, getAuthHeaders());
      toast.success(res.data.message || 'Audience deleted');
      setDeleteAudienceConfirm(null);
      fetchAudiences();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete audience');
    }
  };

  // ---------------- Email Template handlers (welcome + event signup) ----------------

  const openTemplateEditor = async (key = 'welcome') => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/${key}`, getAuthHeaders());
      setCurrentTemplateKey(key);
      setWelcomeTemplate(res.data);
      setTemplateForm({
        subject: res.data.subject || '',
        body_html: res.data.body_html || '',
        body_text: res.data.body_text || '',
      });
      setTemplateTestEmail('');
      setShowTemplateEditor(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to load '${key}' template`);
    }
  };
  const openWelcomeTemplateEditor = () => openTemplateEditor('welcome');

  const handleSaveTemplate = async () => {
    if (!templateForm.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!templateForm.body_html.trim()) {
      toast.error('HTML body is required');
      return;
    }
    setIsSavingTemplate(true);
    try {
      const res = await axios.put(
        `${API}/admin/email-templates/${currentTemplateKey}`,
        {
          subject: templateForm.subject.trim(),
          body_html: templateForm.body_html,
          body_text: templateForm.body_text,
        },
        getAuthHeaders()
      );
      toast.success(res.data.message || 'Template saved');
      setWelcomeTemplate(res.data.template);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleSendTemplateTest = async () => {
    const to = templateTestEmail.trim();
    if (!to || !to.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setIsSendingTemplateTest(true);
    try {
      const res = await axios.post(
        `${API}/admin/email-templates/${currentTemplateKey}/test`,
        { to, first_name: 'Kathy' },
        getAuthHeaders()
      );
      if (res.data.success) {
        toast.success(`Test email sent to ${to}`);
      } else {
        toast.error(`Send failed: ${res.data.error || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test');
    } finally {
      setIsSendingTemplateTest(false);
    }
  };

  // Fetch newsletters when tab changes
  useEffect(() => {
    if (activeTab === 'newsletters' && token) {
      fetchNewsletters();
      fetchAudiences();
    }
  }, [activeTab, token]);

  // Bulk tag management functions
  const handleSelectContact = (contactId, checked) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(contactId);
      } else {
        newSet.delete(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(new Set(filteredContacts.map(c => c.id)));
    } else {
      setSelectedContacts(new Set());
    }
  };

  const handleBulkTagUpdate = async () => {
    if (!bulkTags.trim() && bulkTagOperation !== 'remove') {
      toast.error('Please enter at least one tag');
      return;
    }
    
    const tags = bulkTags.split(',').map(t => t.trim()).filter(t => t);
    
    if (tags.length === 0 && bulkTagOperation !== 'remove') {
      toast.error('Please enter valid tags');
      return;
    }
    
    setIsBulkUpdating(true);
    try {
      const response = await axios.post(`${API}/admin/contacts/bulk-tags`, {
        contact_ids: Array.from(selectedContacts),
        tags: tags,
        operation: bulkTagOperation
      }, getAuthHeaders());
      
      toast.success(response.data.message);
      setShowBulkTagDialog(false);
      setBulkTags('');
      setSelectedContacts(new Set());
      fetchData();
      // Refresh available tags
      const tagsRes = await axios.get(`${API}/admin/tags`, getAuthHeaders());
      setAvailableTags(tagsRes.data.tags || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tags');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleDeleteTag = async (tagName) => {
    if (!window.confirm(`Are you sure you want to remove the tag "${tagName}" from ALL contacts?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/tags/${encodeURIComponent(tagName)}`, getAuthHeaders());
      toast.success(response.data.message);
      fetchData();
      const tagsRes = await axios.get(`${API}/admin/tags`, getAuthHeaders());
      setAvailableTags(tagsRes.data.tags || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const handleRenameTag = async () => {
    if (!newTagName.trim()) {
      toast.error('Please enter a new tag name');
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/admin/tags/${encodeURIComponent(tagToRename)}/rename?new_tag=${encodeURIComponent(newTagName.trim())}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setTagToRename(null);
      setNewTagName('');
      fetchData();
      const tagsRes = await axios.get(`${API}/admin/tags`, getAuthHeaders());
      setAvailableTags(tagsRes.data.tags || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename tag');
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.first_name?.toLowerCase().includes(searchLower) ||
      contact.last_name?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.business_name?.toLowerCase().includes(searchLower) ||
      contact.message?.toLowerCase().includes(searchLower) ||
      contact.tags?.some(tag => tag.toLowerCase().includes(searchLower));
    
    const matchesSubject = subjectFilter === 'all' || contact.subject === subjectFilter;
    const matchesTag = tagFilter === 'all' || (contact.tags && contact.tags.includes(tagFilter));
    
    return matchesSearch && matchesSubject && matchesTag;
  });

  // Get all unique tags from contacts
  const allTags = [...new Set(contacts.flatMap(c => c.tags || []))].sort();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading && !stats) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-serif font-semibold">Admin Dashboard</h1>
              <p className="text-muted-foreground text-sm">Manage contacts, gallery, and more</p>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'contacts' && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowImportDialog(true)}
                  data-testid="import-contacts-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Contacts
                </Button>
              )}
              {activeTab === 'gallery' && galleryStats.can_add && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddGalleryDialog(true)}
                  data-testid="add-gallery-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Image
                </Button>
              )}
              {activeTab === 'newsletters' && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowNewsletterComposer(true)}
                  data-testid="create-newsletter-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Newsletter
                </Button>
              )}
              <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="contacts" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="newsletters" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" />
              Newsletters
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Image className="w-4 h-4 mr-2" />
              Gallery ({galleryStats.total}/{galleryStats.max_items})
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-events">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="auto-emails" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-auto-emails">
              <Mail className="w-4 h-4 mr-2" />
              Edit Auto-Emails
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="space-y-6">
            <ContactsTab
              token={token}
              contactView={contactView}
              setContactView={setContactView}
              stats={stats}
              filteredContacts={filteredContacts}
              allTags={allTags}
              selectedContacts={selectedContacts}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              subjectFilter={subjectFilter}
              setSubjectFilter={setSubjectFilter}
              tagFilter={tagFilter}
              setTagFilter={setTagFilter}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              subjectLabels={subjectLabels}
              formatDate={formatDate}
              setShowTagManagement={setShowTagManagement}
              setBulkTagOperation={setBulkTagOperation}
              setShowBulkTagDialog={setShowBulkTagDialog}
              setSelectedContacts={setSelectedContacts}
              handleSelectAll={handleSelectAll}
              handleSelectContact={handleSelectContact}
              setSelectedContact={setSelectedContact}
              setEditingContact={setEditingContact}
              setDeleteConfirm={setDeleteConfirm}
              handleArchive={handleArchive}
              handleUnarchive={handleUnarchive}
              onContactUpdated={fetchData}
            />
          </TabsContent>

          {/* Gallery Tab */}
          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <GalleryTab
              token={token}
              galleryStats={galleryStats}
              galleryItems={galleryItems}
              isLoadingGallery={isLoadingGallery}
              setShowAddGalleryDialog={setShowAddGalleryDialog}
              setEditingGalleryItem={setEditingGalleryItem}
              setDeleteGalleryConfirm={setDeleteGalleryConfirm}
              onUploaded={fetchGallery}
            />
          </TabsContent>


          {/* Newsletter Tab */}
          <TabsContent value="newsletters" className="space-y-6">
            {/* Newsletter Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Drafts</p>
                    <p className="text-2xl font-semibold">{newsletters.filter(n => n.status === 'draft').length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Send className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sent</p>
                    <p className="text-2xl font-semibold">{newsletters.filter(n => n.status === 'sent').length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Recipients</p>
                    <p className="text-2xl font-semibold">
                      {newsletters.filter(n => n.status === 'sent').reduce((sum, n) => sum + (n.recipient_count || 0), 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Newsletter List */}
            {isLoadingNewsletters ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : newsletters.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No newsletters yet</p>
                <Button onClick={() => setShowNewsletterComposer(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Newsletter
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Subject</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Recipients</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletters.map((newsletter) => (
                        <tr key={newsletter.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="px-6 py-4">
                            <Badge variant={newsletter.status === 'sent' ? 'default' : 'secondary'}>
                              {newsletter.status === 'sent' ? 'Sent' : 'Draft'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 font-medium">{newsletter.subject}</td>
                          <td className="px-6 py-4">
                            {newsletter.status === 'sent' ? (
                              <div className="space-y-1 text-sm" data-testid={`newsletter-row-stats-${newsletter.id}`}>
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                  <span className="font-medium text-green-700" data-testid={`newsletter-sent-count-${newsletter.id}`}>
                                    {newsletter.sent_count ?? 0} sent
                                  </span>
                                  <span className="text-muted-foreground">/ {newsletter.recipient_count}</span>
                                </div>
                                {(newsletter.failed_count ?? 0) > 0 && (
                                  <div className="flex items-center gap-2" data-testid={`newsletter-failed-count-${newsletter.id}`}>
                                    <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                                    <span className="font-medium text-destructive">
                                      {newsletter.failed_count} failed
                                    </span>
                                  </div>
                                )}
                                {/* Show "sending..." only if the newsletter was sent within the last 3 minutes AND hasn't reported any counts yet */}
                                {(newsletter.sent_count ?? 0) === 0 && (newsletter.failed_count ?? 0) === 0 && newsletter.sent_at &&
                                  ((Date.now() - new Date(newsletter.sent_at).getTime()) < 3 * 60 * 1000) && (
                                  <span className="text-xs text-muted-foreground italic">sending…</span>
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline">{newsletter.recipient_filter}</Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">
                            {newsletter.status === 'sent' 
                              ? formatDate(newsletter.sent_at) 
                              : formatDate(newsletter.updated_at || newsletter.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {newsletter.status === 'draft' ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingNewsletter(newsletter)}
                                    data-testid={`newsletter-edit-${newsletter.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePreviewRecipients(newsletter.id)}
                                    data-testid={`newsletter-preview-${newsletter.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700"
                                    onClick={() => setSendConfirm(newsletter)}
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteNewsletter(newsletter.id)}
                                    data-testid={`newsletter-delete-${newsletter.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    // Fetch the freshest copy so sent_count / failed_count reflect the latest background-task state
                                    try {
                                      const res = await axios.get(`${API}/admin/newsletters/${newsletter.id}`, getAuthHeaders());
                                      setNewsletterPreview({
                                        filter: res.data.recipient_filter,
                                        count: res.data.recipient_count,
                                        newsletter: res.data,
                                      });
                                    } catch (err) {
                                      setNewsletterPreview({
                                        filter: newsletter.recipient_filter,
                                        count: newsletter.recipient_count,
                                        newsletter,
                                      });
                                    }
                                  }}
                                  data-testid={`newsletter-view-details-${newsletter.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View Details
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="events" className="space-y-6" data-testid="admin-tab-content-events">
            <AdminEventsTab token={token} />
          </TabsContent>

          <TabsContent value="auto-emails" className="space-y-6" data-testid="admin-tab-content-auto-emails">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-serif text-2xl">
                  <Mail className="w-6 h-6 text-primary" />
                  Auto-Emails
                </CardTitle>
                <CardDescription>
                  These letters go out automatically when someone signs up on the site. Edit the copy anytime — placeholders like <code className="px-1 py-0.5 bg-muted rounded text-xs">{'{{FIRST_NAME}}'}</code> are filled in at send time.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  {
                    key: 'welcome',
                    title: 'Newsletter Welcome Email',
                    trigger: 'Sent when someone subscribes to the newsletter.',
                    testid: 'auto-email-edit-welcome',
                  },
                  {
                    key: 'event_signup_juried',
                    title: 'Event Signup — Juried',
                    trigger: 'Sent when someone applies via the CTA form on a juried event page.',
                    testid: 'auto-email-edit-juried',
                  },
                  {
                    key: 'event_signup_open',
                    title: 'Event Signup — Open (Non-juried)',
                    trigger: 'Sent when someone signs up via the CTA form on a non-juried event page.',
                    testid: 'auto-email-edit-open',
                  },
                  {
                    key: 'ams_kids_young_ceo',
                    title: 'AMS Kids — Young CEO Welcome',
                    trigger: "Sent when a parent signs up their child via the AMS Kids page.",
                    testid: 'auto-email-edit-ams-kids',
                  },
                ].map((row) => (
                  <div
                    key={row.key}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 border rounded-lg hover:border-primary/40 transition-colors bg-card"
                    data-testid={`auto-email-row-${row.key}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg leading-tight">{row.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{row.trigger}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => openTemplateEditor(row.key)}
                      data-testid={row.testid}
                      className="sm:ml-auto"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* View Contact Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Contact Details</DialogTitle>
            <DialogDescription>
              Full information and history for this contact
            </DialogDescription>
          </DialogHeader>
          
          {selectedContact && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedContact.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedContact.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{selectedContact.phone || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Subject</Label>
                  <Badge variant="outline">{subjectLabels[selectedContact.subject] || selectedContact.subject}</Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Date Submitted</Label>
                  <p className="font-medium">{formatDate(selectedContact.created_at)}</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Message</Label>
                <div className="mt-2 p-4 bg-muted/30 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {selectedContact.notes && (
                <div>
                  <Label className="text-muted-foreground">Admin Notes</Label>
                  <div className="mt-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="whitespace-pre-wrap">{selectedContact.notes}</p>
                  </div>
                </div>
              )}

              {selectedContact.tags?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedContact.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* History */}
              {selectedContact.history?.length > 0 && (
                <div>
                  <button
                    onClick={() => setExpandedHistory(prev => ({
                      ...prev,
                      [selectedContact.id]: !prev[selectedContact.id]
                    }))}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    <Clock className="w-4 h-4" />
                    History ({selectedContact.history.length} events)
                    {expandedHistory[selectedContact.id] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {expandedHistory[selectedContact.id] && (
                    <div className="mt-3 space-y-2">
                      {selectedContact.history.slice().reverse().map((event, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm p-3 bg-muted/30 rounded-lg">
                          {event.action === 'newsletter_sent' ? (
                            <Mail className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium capitalize">
                              {event.action === 'newsletter_sent' 
                                ? 'Newsletter Sent' 
                                : event.action.replace('_', ' ')}
                            </p>
                            {event.newsletter_subject && (
                              <p className="text-foreground">
                                "{event.newsletter_subject}"
                              </p>
                            )}
                            <p className="text-muted-foreground">
                              by {event.by} • {formatDate(event.timestamp)}
                            </p>
                            {event.changes && (
                              <p className="text-muted-foreground">
                                Changed: {event.changes.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="gap-2">
                {!selectedContact.read && (
                  <Button variant="outline" onClick={() => {
                    handleMarkRead(selectedContact.id);
                    setSelectedContact(null);
                  }}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                )}
                <Button variant="outline" onClick={() => {
                  setEditingContact(selectedContact);
                  setSelectedContact(null);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {selectedContact.archived ? (
                  <Button variant="outline" onClick={() => handleUnarchive(selectedContact.id)}>
                    <ArchiveRestore className="w-4 h-4 mr-2" />
                    Restore
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => handleArchive(selectedContact.id)}>
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={!!editingContact} onOpenChange={() => setEditingContact(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Edit Contact</DialogTitle>
            <DialogDescription>
              Update contact information and add notes
            </DialogDescription>
          </DialogHeader>
          
          {editingContact && (
            <EditContactForm
              contact={editingContact}
              onSave={(data) => handleUpdate(editingContact.id, data)}
              onCancel={() => setEditingContact(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the contact
              "{deleteConfirm?.name}" and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Contacts Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => !open && resetImportDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Contacts from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to import contacts into your database. Download the sample template to see the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Download Sample Button */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Need a template?</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Download our sample CSV to see the correct format for importing contacts.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDownloadSample}
                    data-testid="download-sample-csv"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Sample CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-3">
              <Label>Select CSV File</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-file-input"
                  data-testid="csv-file-input"
                />
                <label 
                  htmlFor="csv-file-input" 
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  {importFile ? (
                    <div className="text-center">
                      <p className="font-medium text-primary">{importFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium">Click to select a CSV file</p>
                      <p className="text-sm text-muted-foreground">or drag and drop</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`rounded-lg p-4 ${importResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start gap-3">
                  {importResult.success ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${importResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                      {importResult.success ? 'Import Complete!' : 'Import Issues'}
                    </p>
                    <div className="text-sm mt-1 space-y-1">
                      <p>Total rows processed: {importResult.total_rows}</p>
                      <p className="text-emerald-700">Imported: {importResult.imported}</p>
                      {importResult.skipped > 0 && (
                        <p className="text-amber-700">Skipped (empty rows): {importResult.skipped}</p>
                      )}
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium text-red-700">Errors:</p>
                          <ul className="list-disc list-inside text-red-600">
                            {importResult.errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CSV Format Info */}
            <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
              <p className="font-medium mb-1">Accepted CSV columns:</p>
              <p>first_name, last_name, email, phone, business_name, tags</p>
              <p className="mt-1 text-xs">Note: All fields are optional. Tags should be comma-separated.</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={resetImportDialog}>
              {importResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!importResult?.success && (
              <Button 
                onClick={handleImport} 
                disabled={!importFile || isImporting}
                data-testid="import-submit-btn"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Contacts
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Gallery Item Dialog */}
      <Dialog open={!!editingGalleryItem} onOpenChange={() => setEditingGalleryItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Edit Gallery Item</DialogTitle>
            <DialogDescription>
              Update the title, category, caption, or image URL
            </DialogDescription>
          </DialogHeader>
          
          {editingGalleryItem && (
            <GalleryItemForm
              item={editingGalleryItem}
              onSave={(data) => handleUpdateGalleryItem(editingGalleryItem.id, data)}
              onCancel={() => setEditingGalleryItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Gallery Item Dialog */}
      <Dialog open={showAddGalleryDialog} onOpenChange={setShowAddGalleryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Gallery Image</DialogTitle>
            <DialogDescription>
              Add a new image to the gallery ({galleryStats.total}/{galleryStats.max_items} used)
            </DialogDescription>
          </DialogHeader>
          
          <GalleryItemForm
            onSave={handleAddGalleryItem}
            onCancel={() => setShowAddGalleryDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Gallery Item Confirmation */}
      <AlertDialog open={!!deleteGalleryConfirm} onOpenChange={() => setDeleteGalleryConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gallery Image?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteGalleryConfirm?.title}" from the gallery.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDeleteGalleryItem(deleteGalleryConfirm.id)}
            >
              Delete Image
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Newsletter Composer Dialog */}
      <Dialog open={showNewsletterComposer || !!editingNewsletter} onOpenChange={(open) => {
        if (!open) {
          setShowNewsletterComposer(false);
          setEditingNewsletter(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingNewsletter ? 'Edit Newsletter' : 'Create Newsletter'}
            </DialogTitle>
            <DialogDescription>
              Compose your email and select recipients
            </DialogDescription>
          </DialogHeader>
          
          <NewsletterComposer
            newsletter={editingNewsletter}
            availableTags={availableTags}
            audiences={audiences}
            onSaveAudience={handleSaveAudience}
            onOpenAudienceManager={() => setShowAudienceManager(true)}
            onSave={(data) => handleSaveNewsletter(data, !editingNewsletter)}
            onCancel={() => {
              setShowNewsletterComposer(false);
              setEditingNewsletter(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Newsletter Preview Dialog */}
      <Dialog open={!!newsletterPreview} onOpenChange={() => setNewsletterPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {newsletterPreview?.newsletter ? 'Newsletter Details' : 'Preview Recipients'}
            </DialogTitle>
            <DialogDescription>
              {newsletterPreview?.newsletter 
                ? `Sent on ${formatDate(newsletterPreview.newsletter.sent_at)}`
                : `${newsletterPreview?.count || 0} recipients match this filter`}
            </DialogDescription>
          </DialogHeader>
          
          {newsletterPreview && (
            <div className="space-y-4">
              {newsletterPreview.newsletter && (
                <>
                  {/* Send Summary */}
                  <div className="grid grid-cols-3 gap-3" data-testid="newsletter-details-summary">
                    <div className="p-3 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground">Total targeted</p>
                      <p className="text-lg font-semibold">{newsletterPreview.newsletter.recipient_count ?? 0}</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                      <p className="text-xs text-green-700 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Delivered
                      </p>
                      <p className="text-lg font-semibold text-green-700" data-testid="newsletter-details-sent">
                        {newsletterPreview.newsletter.sent_count ?? 0}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg border ${(newsletterPreview.newsletter.failed_count ?? 0) > 0 ? 'bg-destructive/10 border-destructive/30' : 'bg-muted/30'}`}>
                      <p className={`text-xs flex items-center gap-1 ${(newsletterPreview.newsletter.failed_count ?? 0) > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        <AlertCircle className="w-3 h-3" />
                        Failed
                      </p>
                      <p className={`text-lg font-semibold ${(newsletterPreview.newsletter.failed_count ?? 0) > 0 ? 'text-destructive' : ''}`} data-testid="newsletter-details-failed">
                        {newsletterPreview.newsletter.failed_count ?? 0}
                      </p>
                    </div>
                  </div>

                  {/* Failed recipients */}
                  {newsletterPreview.newsletter.failed_recipients && newsletterPreview.newsletter.failed_recipients.length > 0 && (
                    <div className="border border-destructive/30 rounded-lg bg-destructive/5 p-3" data-testid="newsletter-details-failures">
                      <h4 className="font-semibold text-destructive text-sm mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Failed recipients ({newsletterPreview.newsletter.failed_recipients.length})
                      </h4>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-muted-foreground">
                            <tr>
                              <th className="text-left py-1 pr-3">Email</th>
                              <th className="text-left py-1">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {newsletterPreview.newsletter.failed_recipients.map((f, idx) => (
                              <tr key={idx} className="border-t border-destructive/20">
                                <td className="py-1.5 pr-3 font-mono text-xs break-all">{f.email}</td>
                                <td className="py-1.5 text-xs text-destructive">{f.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Subject: {newsletterPreview.newsletter.subject}</h4>
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: newsletterPreview.newsletter.content }}
                    />
                    {newsletterPreview.newsletter.attachments?.length > 0 && (
                      <div className="mt-4 pt-3 border-t" data-testid="newsletter-preview-attachments">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                          Attachments ({newsletterPreview.newsletter.attachments.length})
                        </p>
                        <ul className="space-y-1 text-sm">
                          {newsletterPreview.newsletter.attachments.map((a) => (
                            <li key={a.storage_path} className="flex items-center gap-2">
                              <Paperclip className="w-3.5 h-3.5 text-primary" />
                              <a
                                href={`${API}/files/storage/${a.storage_path}`}
                                target="_blank"
                                rel="noreferrer"
                                className="underline hover:text-primary"
                              >
                                {a.filename}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">
                    Recipients ({newsletterPreview.count})
                  </h4>
                  <Badge variant="outline">Filter: {newsletterPreview.filter}</Badge>
                </div>
                
                {newsletterPreview.recipients && newsletterPreview.recipients.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-left px-3 py-2">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletterPreview.recipients.map((r, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2">{r.name || r.first_name || 'N/A'}</td>
                            <td className="px-3 py-2">{r.email}</td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-xs">{r.type}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    {newsletterPreview.newsletter ? 'Recipient list not available for sent newsletters' : 'No recipients found'}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Newsletter Confirmation */}
      <AlertDialog open={!!sendConfirm} onOpenChange={() => setSendConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Newsletter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send "{sendConfirm?.subject}" to all recipients matching the filter "{sendConfirm?.recipient_filter}". 
              This action cannot be undone.
            </AlertDialogDescription>
            {sendConfirm?.attachments?.length > 0 && (
              <div className="text-sm text-muted-foreground" data-testid="send-confirm-attachments">
                <p className="font-medium text-foreground flex items-center gap-1.5 mb-1">
                  <Paperclip className="w-3.5 h-3.5" />
                  {sendConfirm.attachments.length} attachment{sendConfirm.attachments.length > 1 ? 's' : ''} included
                </p>
                <ul className="list-disc pl-5">
                  {sendConfirm.attachments.map((a) => <li key={a.storage_path}>{a.filename}</li>)}
                </ul>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={() => handleSendNewsletter(sendConfirm.id)}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Newsletter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Tag Update Dialog */}
      <Dialog
        open={showBulkTagDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowBulkTagDialog(false);
            setBulkTags('');
          }
        }}
      >
        <DialogContent className="max-w-lg" data-testid="bulk-tag-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkTagOperation === 'add' && 'Add Tags to Selected Contacts'}
              {bulkTagOperation === 'replace' && 'Replace Tags on Selected Contacts'}
              {bulkTagOperation === 'remove' && 'Remove Tags from Selected Contacts'}
            </DialogTitle>
            <DialogDescription>
              {bulkTagOperation === 'add' &&
                `Add one or more tags to ${selectedContacts.size} selected contact${selectedContacts.size > 1 ? 's' : ''}. Existing tags will be preserved.`}
              {bulkTagOperation === 'replace' &&
                `Replace ALL existing tags on ${selectedContacts.size} selected contact${selectedContacts.size > 1 ? 's' : ''} with the tags below.`}
              {bulkTagOperation === 'remove' &&
                `Remove the specified tags from ${selectedContacts.size} selected contact${selectedContacts.size > 1 ? 's' : ''}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bulk-tags-input">Tags (comma-separated)</Label>
              <Input
                id="bulk-tags-input"
                data-testid="bulk-tags-input"
                placeholder="e.g., priority, vip, follow-up"
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple tags with commas.
              </p>
            </div>

            {availableTags.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {bulkTagOperation === 'remove' ? 'Click to select tags to remove:' : 'Click an existing tag to add it:'}
                </Label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded border">
                  {availableTags.map((tag) => {
                    const current = bulkTags.split(',').map((t) => t.trim()).filter(Boolean);
                    const isSelected = current.includes(tag);
                    return (
                      <Badge
                        key={tag}
                        variant={isSelected ? 'default' : 'outline'}
                        className="cursor-pointer hover:bg-primary/10"
                        onClick={() => {
                          if (isSelected) {
                            setBulkTags(current.filter((t) => t !== tag).join(', '));
                          } else {
                            setBulkTags([...current, tag].join(', '));
                          }
                        }}
                        data-testid={`bulk-tag-suggestion-${tag}`}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {bulkTagOperation === 'replace' && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Warning:</strong> This will remove all existing tags from the selected contacts and replace them with the tags above.
                  Leave empty to clear all tags.
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBulkTagDialog(false);
                setBulkTags('');
              }}
              disabled={isBulkUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkTagUpdate}
              disabled={isBulkUpdating}
              data-testid="bulk-tag-submit-btn"
            >
              {isBulkUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  {bulkTagOperation === 'add' && <Plus className="w-4 h-4 mr-2" />}
                  {bulkTagOperation === 'replace' && <Edit className="w-4 h-4 mr-2" />}
                  {bulkTagOperation === 'remove' && <Trash2 className="w-4 h-4 mr-2" />}
                  Apply to {selectedContacts.size} contact{selectedContacts.size > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Tags Dialog */}
      <Dialog
        open={showTagManagement}
        onOpenChange={(open) => {
          if (!open) {
            setShowTagManagement(false);
            setTagToRename(null);
            setNewTagName('');
          }
        }}
      >
        <DialogContent className="max-w-xl" data-testid="manage-tags-dialog">
          <DialogHeader>
            <DialogTitle>Manage Tags</DialogTitle>
            <DialogDescription>
              Rename or delete tags across all contacts. Changes affect every contact that uses the tag.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {allTags.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No tags have been created yet.</p>
              </div>
            ) : (
              allTags.map((tag) => {
                const isRenaming = tagToRename === tag;
                const usageCount = contacts.filter((c) => c.tags?.includes(tag)).length;
                return (
                  <div
                    key={tag}
                    className="flex items-center justify-between gap-3 p-3 bg-muted/30 rounded border"
                    data-testid={`manage-tag-row-${tag}`}
                  >
                    {isRenaming ? (
                      <>
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            placeholder="New tag name"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameTag();
                              if (e.key === 'Escape') {
                                setTagToRename(null);
                                setNewTagName('');
                              }
                            }}
                            data-testid={`rename-tag-input-${tag}`}
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            onClick={handleRenameTag}
                            data-testid={`rename-tag-save-${tag}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setTagToRename(null);
                              setNewTagName('');
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Badge variant="secondary" className="truncate">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {usageCount} contact{usageCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTagToRename(tag);
                              setNewTagName(tag);
                            }}
                            data-testid={`rename-tag-btn-${tag}`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Rename
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteTag(tag)}
                            data-testid={`delete-tag-btn-${tag}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTagManagement(false);
                setTagToRename(null);
                setNewTagName('');
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Audiences Dialog */}
      <Dialog
        open={showAudienceManager}
        onOpenChange={(open) => {
          if (!open) {
            setShowAudienceManager(false);
            setEditingAudience(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="manage-audiences-dialog">
          <DialogHeader>
            <DialogTitle>Saved Audiences</DialogTitle>
            <DialogDescription>
              Named recipient filters you can reuse on any newsletter.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {audiences.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No audiences saved yet.</p>
                <p className="text-xs mt-1">Create one from the newsletter composer.</p>
              </div>
            ) : (
              audiences.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded border"
                  data-testid={`audience-row-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="font-medium">
                        <Users className="w-3 h-3 mr-1" />
                        {a.name}
                      </Badge>
                      {typeof a.recipient_count === 'number' && (
                        <span className="text-xs text-muted-foreground">
                          {a.recipient_count} recipient{a.recipient_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {a.description && (
                      <p className="text-xs text-muted-foreground mt-1">{a.description}</p>
                    )}
                    <p className="text-xs font-mono text-muted-foreground/80 mt-1 truncate">
                      Filter: {a.filter}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingAudience(a)}
                      data-testid={`audience-edit-${a.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteAudienceConfirm(a)}
                      data-testid={`audience-delete-${a.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAudienceManager(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Audience Dialog */}
      <Dialog
        open={!!editingAudience}
        onOpenChange={(open) => !open && setEditingAudience(null)}
      >
        <DialogContent className="max-w-lg" data-testid="edit-audience-dialog">
          <DialogHeader>
            <DialogTitle>Edit Audience</DialogTitle>
            <DialogDescription>Rename or update this saved audience.</DialogDescription>
          </DialogHeader>
          {editingAudience && (
            <EditAudienceForm
              audience={editingAudience}
              onSave={async (data) => {
                const updated = await handleUpdateAudience(editingAudience.id, data);
                if (updated) setEditingAudience(null);
              }}
              onCancel={() => setEditingAudience(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Audience Confirmation */}
      <AlertDialog
        open={!!deleteAudienceConfirm}
        onOpenChange={(open) => !open && setDeleteAudienceConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete audience?</AlertDialogTitle>
            <AlertDialogDescription>
              {`This will permanently delete the audience "${deleteAudienceConfirm?.name}". Newsletters previously sent to it are not affected.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => handleDeleteAudience(deleteAudienceConfirm.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Welcome Email Template Editor Dialog */}
      <Dialog
        open={showTemplateEditor}
        onOpenChange={(open) => !open && setShowTemplateEditor(false)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="welcome-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {welcomeTemplate?.name || 'Edit Email Template'}
            </DialogTitle>
            <DialogDescription>
              {welcomeTemplate?.description ||
                "Sent automatically. Use the placeholders below — they'll be replaced at send time."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Placeholder reference */}
            <div className="p-3 bg-muted/40 rounded border text-xs space-y-1">
              <p className="font-semibold text-muted-foreground">Available placeholders:</p>
              <div className="flex flex-wrap gap-2">
                {(welcomeTemplate?.placeholders || ['{{GREETING}}', '{{APP_URL}}', '{{FIRST_NAME}}']).map((p) => (
                  <code key={p} className="px-2 py-0.5 bg-background rounded border font-mono">{p}</code>
                ))}
              </div>
              <p className="text-muted-foreground pt-1">
                The CAN-SPAM unsubscribe footer + physical address are appended automatically — you don't need to add them here.
              </p>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="welcome-template-subject">Subject</Label>
              <Input
                id="welcome-template-subject"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Welcome to the Artisan Market & Studio Newsletter!"
                data-testid="welcome-template-subject"
              />
            </div>

            {/* HTML body */}
            <div className="space-y-2">
              <Label htmlFor="welcome-template-html">
                HTML body
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  (raw HTML — placeholders use <code>{'{{NAME}}'}</code> syntax)
                </span>
              </Label>
              <Textarea
                id="welcome-template-html"
                value={templateForm.body_html}
                onChange={(e) => setTemplateForm({ ...templateForm, body_html: e.target.value })}
                rows={14}
                className="font-mono text-xs"
                data-testid="welcome-template-html"
              />
            </div>

            {/* Plain text body */}
            <div className="space-y-2">
              <Label htmlFor="welcome-template-text">
                Plain-text fallback
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  (shown by mail clients that don't render HTML)
                </span>
              </Label>
              <Textarea
                id="welcome-template-text"
                value={templateForm.body_text}
                onChange={(e) => setTemplateForm({ ...templateForm, body_text: e.target.value })}
                rows={6}
                className="text-xs"
                data-testid="welcome-template-text"
              />
            </div>

            {/* Test send */}
            <div className="p-3 bg-muted/30 rounded border space-y-2">
              <Label className="text-sm">Send a test render to:</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={templateTestEmail}
                  onChange={(e) => setTemplateTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1"
                  data-testid="welcome-template-test-email"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendTemplateTest}
                  disabled={isSendingTemplateTest}
                  data-testid="welcome-template-test-btn"
                >
                  {isSendingTemplateTest ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" />Sending</>
                  ) : (
                    <><Send className="w-4 h-4 mr-1" />Send test</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Test sends use the <strong>currently saved</strong> version on the server. Save your changes first if you want to test edits.
              </p>
            </div>

            {welcomeTemplate?.updated_at && (
              <p className="text-xs text-muted-foreground italic">
                Last updated {new Date(welcomeTemplate.updated_at).toLocaleString()}
                {welcomeTemplate.updated_by && ` by ${welcomeTemplate.updated_by}`}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateEditor(false)} disabled={isSavingTemplate}>
              Close
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={isSavingTemplate}
              data-testid="welcome-template-save-btn"
            >
              {isSavingTemplate ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save changes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
